// assets/custom-age-tabs.js

(function () {
  'use strict';

  const SELECTORS = {
    root:   '[data-component="age-tabs"]',
    strip:  '[role="tablist"]',
    tab:    '[role="tab"]',
    panel:  '[role="tabpanel"]',
    atcBtn: '[data-at-atc]',
  };

  class AgeTabs {
    constructor(el) {
      this.el     = el;
      this.strip  = el.querySelector(SELECTORS.strip);
      this.tabs   = Array.from(el.querySelectorAll(SELECTORS.tab));
      this.panels = Array.from(el.querySelectorAll(SELECTORS.panel));
      this._active = 0;
      this._init();
    }

    _init() {
      this._bindTabs();
      this._bindKeyboard();
      this._bindATC();
      this._initFromHash();
    }

    // ── Tab clicks ───────────────────────────────────────────────────────────
    _bindTabs() {
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this._activate(i));
      });
    }

    // ── Keyboard navigation (ARIA tab pattern) ───────────────────────────────
    _bindKeyboard() {
      if (!this.strip) return;
      this.strip.addEventListener('keydown', e => {
        const len = this.tabs.length;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          this._activate((this._active + 1) % len);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          this._activate((this._active - 1 + len) % len);
        } else if (e.key === 'Home') {
          e.preventDefault();
          this._activate(0);
        } else if (e.key === 'End') {
          e.preventDefault();
          this._activate(len - 1);
        }
      });
    }

    // ── Activate a tab by index ──────────────────────────────────────────────
    _activate(index, pushHash = true) {
      if (index === this._active) return;

      const prevTab   = this.tabs[this._active];
      const prevPanel = this.panels[this._active];
      if (prevTab)   { prevTab.setAttribute('aria-selected', 'false'); prevTab.classList.remove('hbk-at__tab--active'); prevTab.tabIndex = -1; }
      if (prevPanel) { prevPanel.classList.remove('hbk-at__panel--active'); prevPanel.setAttribute('aria-hidden', 'true'); }

      this._active = index;
      const tab   = this.tabs[index];
      const panel = this.panels[index];
      if (tab)   { tab.setAttribute('aria-selected', 'true'); tab.classList.add('hbk-at__tab--active'); tab.tabIndex = 0; tab.focus(); }
      if (panel) { panel.classList.add('hbk-at__panel--active'); panel.removeAttribute('aria-hidden'); }

      // Scroll tab into view on mobile
      tab?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });

      // Update URL hash for deep-linking
      if (pushHash) {
        const slug = this._tabSlug(tab);
        if (slug) {
          try { history.replaceState(null, '', `#tab-${slug}`); } catch (_) {}
        }
      }
    }

    _tabSlug(tab) {
      if (!tab) return '';
      const label = tab.querySelector('.hbk-at__tab-label');
      if (!label) return tab.dataset.tabId || '';
      return label.textContent
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
    }

    // ── Deep-link from URL hash ──────────────────────────────────────────────
    _initFromHash() {
      const hash = window.location.hash;
      if (!hash.startsWith('#tab-')) return;
      const slug = hash.slice(5);
      this.tabs.forEach((tab, i) => {
        if (this._tabSlug(tab) === slug && i !== this._active) {
          // Swap without pushing hash again
          const prevTab   = this.tabs[this._active];
          const prevPanel = this.panels[this._active];
          if (prevTab)   { prevTab.setAttribute('aria-selected', 'false'); prevTab.classList.remove('hbk-at__tab--active'); prevTab.tabIndex = -1; }
          if (prevPanel) { prevPanel.classList.remove('hbk-at__panel--active'); prevPanel.setAttribute('aria-hidden', 'true'); }
          this._active = i;
          tab.setAttribute('aria-selected', 'true');
          tab.classList.add('hbk-at__tab--active');
          tab.tabIndex = 0;
          this.panels[i]?.classList.add('hbk-at__panel--active');
          this.panels[i]?.removeAttribute('aria-hidden');
        }
      });
    }

    // ── ATC (delegated) ──────────────────────────────────────────────────────
    _bindATC() {
      this.el.addEventListener('click', async e => {
        const btn = e.target.closest(SELECTORS.atcBtn);
        if (!btn || btn.disabled) return;
        const variantId = Number(btn.dataset.variantId);
        if (!variantId) return;

        const original = btn.textContent.trim();
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        btn.textContent = '…';

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.description || 'Error');
          }
          document.dispatchEvent(new CustomEvent('cart:add', { detail: { variantId } }));
          document.dispatchEvent(new CustomEvent('cart:open'));
          btn.textContent = '✓ Added';
          setTimeout(() => { btn.textContent = original; btn.disabled = false; btn.setAttribute('aria-busy', 'false'); }, 2000);
        } catch {
          btn.textContent = 'Error — try again';
          setTimeout(() => { btn.textContent = original; btn.disabled = false; btn.setAttribute('aria-busy', 'false'); }, 2500);
        }
      });
    }

    destroy() {}
  }

  // ── Registry ────────────────────────────────────────────────────────────────
  const instances = new Map();

  function init(scope = document) {
    scope.querySelectorAll(SELECTORS.root).forEach(el => {
      if (instances.has(el)) return;
      instances.set(el, new AgeTabs(el));
    });
  }

  function destroy(scope = document) {
    scope.querySelectorAll(SELECTORS.root).forEach(el => {
      if (!instances.has(el)) return;
      instances.get(el).destroy();
      instances.delete(el);
    });
  }

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load',   e => init(e.target));
  document.addEventListener('shopify:section:unload', e => destroy(e.target));

  // Activate the matching tab when a block is selected in the Theme Editor
  document.addEventListener('shopify:block:select', e => {
    const root = e.target.closest(SELECTORS.root);
    if (!root || !instances.has(root)) return;
    const inst = instances.get(root);
    const idx  = inst.tabs.indexOf(e.target);
    if (idx !== -1) inst._activate(idx, false);
  });

})();
